/* eslint-disable prefer-template */
/* eslint-disable no-use-before-define */
/* eslint-disable max-len */
/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/

import React, { useEffect, useReducer } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { InView } from 'react-intersection-observer';
import {
  useDispatch, useSelector, shallowEqual,
} from 'react-redux';
import {
  withAppManager, LoadingSpinner, unixTimestampToDate,
} from '@ziynx/components/utilities';
import Colors from '@ziynx/theme/colors';
import { Heading, Body } from '@ziynx/components/typography';
import { ContentContainer, PageTemplate } from '@ziynx/components/layout';
import Headline from '@ziynx/components/headlines';
import Card from '@ziynx/components/cards';
import Icon from '@ziynx/components/icons';
import Link from '@ziynx/components/links';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import ActionTypes from '@ziynx/action-types';
import { eventNames } from '@ziynx/analytics';
import * as KrispyTokens from '@krispy-tokens/blue';
import DestructiveModal from '../Modals/DestructiveModal';

type Props = $ReadOnly<{|
  viewport: Viewports,
  /**
   * Navigation stack
   */
  history: Object,
|}>;

type DispatchAction = {
  type: string,
} => void;

const StyledHeadline = styled(Headline)`
  margin-bottom: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;
`;

const OriginalsWrapper = styled.div`
  ${({ isDesktop }) => isDesktop && `margin-top: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;`}
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;
`;

const StyledCard = styled(Card)`
  margin-bottom: ${KrispyTokens.KrispySpacingElementsMdSmall}px;
`;

const EmptyWrapper = styled.div`
  text-align: center;
`;

const IconWrapper = styled.div`
  margin-bottom: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;
`;

const TextWrapper = styled.div`
  margin-bottom: ${KrispyTokens.KrispySpacingElementsHdSmall}px;
`;

function reducer(state: ReducerState, action: ActionType) {
  const { type } = action;

  switch (type) {
  case 'setDestructiveModal': {
    return {
      ...state,
      destructiveModal: action?.destructiveModal,
    };
  }
  default:
    return state;
  }
}

const initialReducerState: ReducerState = {
  destructiveModal: {
    open: false,
  },
};

const OriginalsListScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport, history } = props;

  const isMobileSmall = viewport === 'mobileSmall';

  const dispatch: DispatchAction = useDispatch();

  const [state, dispatchReducer] = useReducer(reducer, initialReducerState);

  const originalsService = useSelector(({ originals }) => originals || {}, shallowEqual);

  useEffect(() => {
    dispatch({
      type: ActionTypes.originals.getAll.START,
      page: 0,
    });
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_originals_list,
    });
  }, []);

  const _onPaginate = (inView): void => {
    if (originalsService?.isLastPage || originalsService?.fetching || !inView) return;
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.scrolled_through_originals_list,
      properties: {
        pageNumber: originalsService?.page + 1,
      },
    });
    dispatch({
      type: ActionTypes.originals.getAll.START,
      page: originalsService?.page + 1,
    });
  };

  const _renderEmpty = () => (
    <EmptyWrapper>
      <IconWrapper>
        <Icon name="video-camera-outlined" small color={Colors.GrayLight} />
      </IconWrapper>
      <TextWrapper>
        <Heading medium color={Colors.Gray}>No items to show</Heading>
      </TextWrapper>
      {!(originalsService?.results ?? []).length
      && (
        <Body medium>
          <Link
            large
            variant="text"
            onClick={() => {
            // eslint-disable-next-line react/prop-types
              history.push('/originals/create', { from: 'originalsList' });
            }}
          >
            Add an item
          </Link>
        </Body>
      )}
    </EmptyWrapper>
  );

  const _renderOriginalsCards = () => originalsService?.results?.map((item, index): React$Node => {
    if (!item) return null;
    const editOriginalPath = '/originals/' + item?._id + '/edit';

    const priorityLabel = 'Priority: ' + item?.priority;

    const publishDateLabel = 'Publish Date: ' + unixTimestampToDate(item?.createdAt);

    const thumbnailImage = item?.thumbnail?.sources?.cropped?.url ?? item?.thumbnail?.sources?.original?.url;
    let bodyLabel = publishDateLabel;

    if (item?.priority) {
      bodyLabel = bodyLabel + ' ' + priorityLabel;
    }

    return (
      <StyledCard
        key={index}
        variant="videoPreview"
        image={{ imageSource: thumbnailImage }}
        title={item?.title}
        subtitle={item?.seriesTitle}
        subtitleColor={Colors.GrayVeryDark}
        body={bodyLabel}
        archived={item?.archived}
        viewport={viewport}
        rightAction={{
          popover: {
            position: 'bottomLeft',
            links: [
              {
                children: 'Edit details',
                large: true,
                onClick: () => { history.push(editOriginalPath, { from: 'OriginalsList' }); },
              },
              {
                children: 'Delete item',
                large: true,
                color: Colors.SecondaryRed,
                onClick: () => {
                  // Don't like this but oh well
                  dispatchReducer({
                    type: 'setDestructiveModal',
                    destructiveModal: {
                      open: true,
                      heading: 'Are you sure that you would like to delete this post?',
                      buttonGroup: {
                        destructive: {
                          children: 'Delete',
                          onClick: () => {
                            dispatch({
                              type: ActionTypes.originals.delete.START,
                              id: item?._id,
                            });
                            dispatchReducer({
                              type: 'setDestructiveModal',
                              destructiveModal: {
                                open: false,
                              },
                            });
                          },
                        },
                        primary: {
                          children: 'Cancel',
                          onClick: () => {
                            dispatchReducer({
                              type: 'setDestructiveModal',
                              destructiveModal: {
                                open: false,
                              },
                            });
                          },
                        },
                      },
                    },
                  });
                },
              },
            ],
          },
        }}
      />
    );
  });

  const _renderLeftContent = () => {
    if (!(originalsService?.results ?? []).length
    && (!originalsService?.fetching)
    ) {
      return <Card variant="list" renderEmpty={_renderEmpty} />;
    }

    return (
      <>
        { _renderOriginalsCards() }
        <InView as="div" onChange={_onPaginate}>
          <SpinnerWrapper>
            {!originalsService?.isLastPage && originalsService?.fetching && <LoadingSpinner />}
          </SpinnerWrapper>
        </InView>
      </>
    );
  };

  const leftSlot = (): React$Node => (
    <>
      <StyledHeadline
        variant="page"
        title={{ text: 'ZIYNX Originals' }}
        squircleButton={isMobileSmall && {
          icon: { size: 'large' },
          onClick: () => history.push('/originals/create', { from: 'OriginalList' }),
        }}
        textLink={!isMobileSmall && {
          large: true,
          children: 'Add new',
          onClick: () => history.push('/originals/create', { from: 'OriginalList' }),
        }}
      />
    </>
  );

  const leftContent = (): React$Node => (
    <OriginalsWrapper isDesktop={viewport === 'desktop'}>
      {_renderLeftContent()}
    </OriginalsWrapper>
  );

  const rightSlot = (): React$Node => (
    <>
      {/* <Headline
        variant="default"
        title={{ text: 'Filter' }}
        style={{ marginBottom: '24px' }}
      />
      <ButtonPillGroup stacked pills={filterButtons} /> */}
    </>
  );

  return (
    <>
      <Helmet>
        <title>Originals | ZIYNX</title>
      </Helmet>
      <DestructiveModal
        open={state?.destructiveModal?.open}
        heading={{ text: state?.destructiveModal?.heading || '' }}
        buttonGroup={state?.destructiveModal?.buttonGroup}
      />
      <ContentContainer offsetTop={86}>
        <PageTemplate
          alwaysShowRightSlot
          viewport={viewport}
          leftSlot={leftSlot()}
          leftContent={leftContent()}
          rightSlot={rightSlot()}
          headerOffset={-1000} // Temporary measure to remove barSticky
        />
      </ContentContainer>
    </>
  );
};

export default (withAppManager(OriginalsListScreen): React$ComponentType<ManagedProps<Props>>);
